import React from "react";
import { useNavigate } from "react-router-dom";
import { showSuccess, showError } from "toast/toast";
import logo from "../assets/images/Black_Version.png";
import "./RestPassword.css";
import { getAuthCookie } from "../utils/authCookies";

const ResetPasswordPage = () => {
  const navigate = useNavigate();
  const [value, setValue] = React.useState({
    email: "",
    old: "",
    next: "",
    confirm: "",
  });

  const [showPasswords, setShowPasswords] = React.useState({
    old: false,
    next: false,
    confirm: false,
  });

  const [showValidationBox, setShowValidationBox] = React.useState(false);
  const [pwdChecks, setPwdChecks] = React.useState({
    length: false,
    uppercase: false,
    lowercase: false,
    digit: false,
    specialChar: false,
  });

  const [isSubmitting, setIsSubmitting] = React.useState(false);

  const validatePwd = (pwd: string) => {
    setPwdChecks({
      length: pwd.length >= 8,
      uppercase: /[A-Z]/.test(pwd),
      lowercase: /[a-z]/.test(pwd),
      digit: /\d/.test(pwd),
      specialChar: /[!@#$%^&*(),.?":{}|<>]/.test(pwd),
    });
  };

  const passwordsMatch =
    value.next && value.confirm && value.next === value.confirm;

  const allValidationsPassed =
    pwdChecks.length &&
    pwdChecks.uppercase &&
    pwdChecks.lowercase &&
    pwdChecks.digit &&
    pwdChecks.specialChar;

  const isEmailValid = value.email && /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(value.email);
  const allFieldsFilled = value.email && value.old && value.next && value.confirm;

  const canUpdatePassword = 
    allFieldsFilled && 
    isEmailValid && 
    passwordsMatch && 
    allValidationsPassed;

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value: val } = e.target;
    setValue((prev) => ({ ...prev, [name]: val }));

    if (name === "next") {
      validatePwd(val);
      setShowValidationBox(true);
    }
  };

  const togglePasswordVisibility = (field: keyof typeof showPasswords) => {
    setShowPasswords((prev) => ({
      ...prev,
      [field]: !prev[field],
    }));
  };

  const handleSubmit = async () => {
    if (!canUpdatePassword) {
      return;
    }

    setIsSubmitting(true);

    try {
      const token = getAuthCookie("sessionToken") || "";
      if (!token) {
        showError("Authentication token not found. Please login again.");
        navigate("/login");
        return;
      }

      const response = await fetch(
        `${process.env.REACT_APP_API_URL}/users/reset-password`,
        {
          method: "PATCH",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${token}`,
          },
          body: JSON.stringify({
            new_password: value.next,
            confirm_password: value.confirm,
            email: value.email,
            old_password: value.old,
          }),
        }
      );

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.message || "Failed to update password");
      }

      const result = await response.json();
      
      // Check for success response structure
      if (result.success && result.data?.message) {
        showSuccess(result.data.message || "Password updated successfully!");
      } else {
        showSuccess("Password updated successfully!");
      }

      // Reset form
      setValue({
        email: "",
        old: "",
        next: "",
        confirm: "",
      });
      setShowValidationBox(false);
      setPwdChecks({
        length: false,
        uppercase: false,
        lowercase: false,
        digit: false,
        specialChar: false,
      });
    } catch (error: any) {
      showError(error.message || "Failed to update password. Please try again.");
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div 
      className="reset-password-container d-flex align-items-start justify-content-center min-vh-100 p-4"
      style={{ 
        backgroundColor: '#f9fafb',
        paddingTop: '3rem',
        fontFamily: 'Roboto, -apple-system, BlinkMacSystemFont, Segoe UI, sans-serif'
      }}
    >
      <div className="w-100" style={{ maxWidth: '28rem' }}>
        {/* Logo/Header Section */}
        <div className="text-center mb-8">
          <h1 
            className="mb-3"
            style={{ 
            //   color: '#2563EB', 
              fontSize: '36px', 
              fontWeight: '500', 
              letterSpacing: '-0.5px',
              marginBottom: '0.75rem'
            }}
          >
                <img src={logo} alt="Logo" style={{ height: "95px", width: "170px" }} />
          </h1>
          <div 
            className="d-inline-flex align-items-center justify-content-center rounded-circle mb-3"
            style={{ 
              width: '64px', 
              height: '64px', 
              backgroundColor: '#dbeafe' 
            }}
          >
            <i 
              className="ri-lock-2-line"
              style={{ 
                color: '#2563EB', 
                fontSize: '32px' 
              }}
            />
          </div>
          <h2 
            className="mb-2"
            style={{ 
              color: '#111827', 
              fontSize: '24px', 
              fontWeight: '500',
              marginBottom: '0.5rem'
            }}
          >
            Reset Your Password
          </h2>
          <p style={{ color: '#6b7280', fontSize: '15px', marginBottom: '0' }}>
            Please enter a new password for your account
          </p>
        </div>

        {/* Form Card */}
        <div 
          className="form-card bg-white rounded-lg p-4"
          style={{ 
            boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -1px rgba(0, 0, 0, 0.06)',
            borderRadius: '0.625rem',
            padding: '2rem'
          }}
        >
          {/* Password Requirements Info */}
          <div 
            className="mb-4 p-3 rounded-lg"
            style={{ 
              backgroundColor: '#eff6ff', 
              border: '1px solid #bfdbfe',
              borderRadius: '0.625rem',
              marginBottom: '1.5rem'
            }}
          >
            <p className="mb-0" style={{ color: '#1e40af', fontSize: '14px', lineHeight: '20px' }}>
              <strong>Password Requirements:</strong> Must be at least 8 characters and include uppercase letter, lowercase letter, number, and symbol.
            </p>
          </div>

          {/* EMAIL */}
          <div className="mb-4">
            <label 
              htmlFor="email" 
              className="d-block mb-2"
              style={{ color: '#374151', fontSize: '14px', fontWeight: '500' }}
            >
              Email <span style={{ color: '#ef4444' }}>*</span>
            </label>
            <input
              type="email"
              id="email"
              name="email"
              className={`form-control rounded-lg ${value.email && !isEmailValid ? "is-invalid" : ""}`}
              style={{
                padding: '0.75rem 1rem',
                borderRadius: '0.625rem',
                borderColor: value.email && !isEmailValid ? '#ef4444' : '#d1d5db',
                fontSize: '1rem',
                transition: 'all 0.2s ease-in-out'
              }}
              value={value.email}
              onChange={handleChange}
              placeholder="Enter your email"
              onFocus={(e) => {
                if (!value.email || isEmailValid) {
                  e.target.style.borderColor = '#9ca3af';
                  e.target.style.boxShadow = '0 0 0 3px rgba(156, 163, 175, 0.1)';
                }
              }}
              onBlur={(e) => {
                e.target.style.borderColor = value.email && !isEmailValid ? '#ef4444' : '#d1d5db';
                e.target.style.boxShadow = 'none';
              }}
            />
            {value.email && !isEmailValid && (
              <p className="mt-2 d-flex align-items-center gap-1" style={{ color: '#ef4444', fontSize: '13px', marginTop: '0.5rem' }}>
                <span>⚠</span> Please enter a valid email address
              </p>
            )}
          </div>

          {/* OLD PASSWORD */}
          <div className="mb-4">
            <label 
              htmlFor="oldPassword" 
              className="d-block mb-2"
              style={{ color: '#374151', fontSize: '14px', fontWeight: '500' }}
            >
              Old Password <span style={{ color: '#ef4444' }}>*</span>
            </label>
            <div className="position-relative">
              <input
                type={showPasswords.old ? "text" : "password"}
                id="oldPassword"
                name="old"
                className="form-control rounded-lg"
                style={{
                  padding: '0.75rem 3rem 0.75rem 1rem',
                  borderRadius: '0.625rem',
                  borderColor: '#d1d5db',
                  fontSize: '1rem',
                  transition: 'all 0.2s ease-in-out'
                }}
                value={value.old}
                onChange={handleChange}
                placeholder="Enter your old password"
                onFocus={(e) => {
                  e.target.style.borderColor = '#9ca3af';
                  e.target.style.boxShadow = '0 0 0 3px rgba(156, 163, 175, 0.1)';
                }}
                onBlur={(e) => {
                  e.target.style.borderColor = '#d1d5db';
                  e.target.style.boxShadow = 'none';
                }}
              />
              <button
                type="button"
                className="btn position-absolute border-0 bg-transparent"
                onClick={() => togglePasswordVisibility("old")}
                style={{ 
                  right: '0.75rem', 
                  top: '50%', 
                  transform: 'translateY(-50%)',
                  zIndex: 10,
                  color: '#6b7280',
                  padding: '0.5rem'
                }}
              >
                <i 
                  className={`ri-${showPasswords.old ? 'eye-off' : 'eye'}-line`}
                  style={{ fontSize: '20px' }}
                />
              </button>
            </div>
          </div>

          {/* NEW PASSWORD */}
          <div className="mb-4">
            <label 
              htmlFor="newPassword" 
              className="d-block mb-2"
              style={{ color: '#374151', fontSize: '14px', fontWeight: '500' }}
            >
              New Password <span style={{ color: '#ef4444' }}>*</span>
            </label>
            <div className="position-relative">
              <input
                type={showPasswords.next ? "text" : "password"}
                id="newPassword"
                name="next"
                className="form-control rounded-lg"
                style={{
                  padding: '0.75rem 3rem 0.75rem 1rem',
                  borderRadius: '0.625rem',
                  borderColor: '#d1d5db',
                  fontSize: '1rem',
                  transition: 'all 0.2s ease-in-out'
                }}
                value={value.next}
                onChange={handleChange}
                onFocus={(e) => {
                  setShowValidationBox(true);
                  e.target.style.borderColor = '#9ca3af';
                  e.target.style.boxShadow = '0 0 0 3px rgba(156, 163, 175, 0.1)';
                }}
                onBlur={(e) => {
                  e.target.style.borderColor = '#d1d5db';
                  e.target.style.boxShadow = 'none';
                }}
                placeholder="Enter your new password"
              />
              <button
                type="button"
                className="btn position-absolute border-0 bg-transparent"
                onClick={() => togglePasswordVisibility("next")}
                style={{ 
                  right: '0.75rem', 
                  top: '50%', 
                  transform: 'translateY(-50%)',
                  zIndex: 10,
                  color: '#6b7280',
                  padding: '0.5rem'
                }}
              >
                <i 
                  className={`ri-${showPasswords.next ? 'eye-off' : 'eye'}-line`}
                  style={{ fontSize: '20px' }}
                />
              </button>
            </div>
          </div>

          {/* Password Strength Indicators */}
          {showValidationBox && value.next && (
            <div 
              className="mb-4 p-3 rounded-lg"
              style={{ 
                backgroundColor: '#f9fafb', 
                border: '1px solid #e5e7eb',
                borderRadius: '0.625rem',
                marginBottom: '1.5rem'
              }}
            >
              <p className="mb-3" style={{ color: '#374151', fontSize: '13px', fontWeight: '500', marginBottom: '0.75rem' }}>
                Password Strength:
              </p>
              <div style={{ display: 'flex', flexDirection: 'column', gap: '0.5rem' }}>
                {[
                  { check: pwdChecks.length, text: 'At least 8 characters' },
                  { check: pwdChecks.uppercase, text: 'One uppercase letter (A-Z)' },
                  { check: pwdChecks.lowercase, text: 'One lowercase letter (a-z)' },
                  { check: pwdChecks.digit, text: 'One number (0-9)' },
                  { check: pwdChecks.specialChar, text: 'One symbol (!@#$%^&*)' }
                ].map((item, idx) => (
                  <div key={idx} className="d-flex align-items-center gap-2">
                    <div 
                      className="flex-shrink-0 rounded-circle d-flex align-items-center justify-content-center"
                      style={{ 
                        width: '20px',
                        height: '20px',
                        backgroundColor: item.check ? '#dcfce7' : '#f3f4f6',
                        border: `2px solid ${item.check ? '#16a34a' : '#d1d5db'}`
                      }}
                    >
                      {item.check && (
                        <svg width="12" height="12" fill="none" viewBox="0 0 24 24" stroke="#16a34a" strokeWidth="3">
                          <path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" />
                        </svg>
                      )}
                    </div>
                    <span style={{ color: item.check ? '#16a34a' : '#6b7280', fontSize: '13px' }}>
                      {item.text}
                    </span>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* CONFIRM PASSWORD */}
          <div className="mb-4">
            <label 
              htmlFor="confirmPassword" 
              className="d-block mb-2"
              style={{ color: '#374151', fontSize: '14px', fontWeight: '500' }}
            >
              Confirm Password <span style={{ color: '#ef4444' }}>*</span>
            </label>
            <div className="position-relative">
              <input
                type={showPasswords.confirm ? "text" : "password"}
                id="confirmPassword"
                name="confirm"
                className={`form-control rounded-lg ${value.confirm && !passwordsMatch ? "is-invalid" : ""}`}
                style={{
                  padding: '0.75rem 3rem 0.75rem 1rem',
                  borderRadius: '0.625rem',
                  borderColor: value.confirm && !passwordsMatch ? '#ef4444' : '#d1d5db',
                  fontSize: '1rem',
                  transition: 'all 0.2s ease-in-out'
                }}
                value={value.confirm}
                onChange={handleChange}
                placeholder="Re-enter your new password"
                onFocus={(e) => {
                  if (!value.confirm || passwordsMatch) {
                    e.target.style.borderColor = '#9ca3af';
                    e.target.style.boxShadow = '0 0 0 3px rgba(156, 163, 175, 0.1)';
                  }
                }}
                onBlur={(e) => {
                  e.target.style.borderColor = value.confirm && !passwordsMatch ? '#ef4444' : '#d1d5db';
                  e.target.style.boxShadow = 'none';
                }}
              />
              <button
                type="button"
                className="btn position-absolute border-0 bg-transparent"
                onClick={() => togglePasswordVisibility("confirm")}
                style={{ 
                  right: '0.75rem', 
                  top: '50%', 
                  transform: 'translateY(-50%)',
                  zIndex: 10,
                  color: '#6b7280',
                  padding: '0.5rem'
                }}
              >
                <i 
                  className={`ri-${showPasswords.confirm ? 'eye-off' : 'eye'}-line`}
                  style={{ fontSize: '20px' }}
                />
              </button>
            </div>
            {value.confirm && !passwordsMatch && (
              <p className="mt-2 d-flex align-items-center gap-1" style={{ color: '#ef4444', fontSize: '13px', marginTop: '0.5rem' }}>
                <span>⚠</span> Passwords do not match
              </p>
            )}
            {value.confirm && passwordsMatch && !showValidationBox && (
              <p className="mt-2 d-flex align-items-center gap-2" style={{ color: '#16a34a', fontSize: '13px', marginTop: '0.5rem' }}>
                <i className="ri-check-line" style={{ fontSize: '16px' }} />
                Passwords match
              </p>
            )}
          </div>

          {/* Submit Button */}
          <button
            type="button"
            disabled={!canUpdatePassword || isSubmitting}
            className="w-100 rounded-lg border-0 text-white"
            onClick={handleSubmit}
            style={{
              padding: '0.75rem 1rem',
              backgroundColor: isSubmitting ? '#9ca3af' : '#2563EB',
              color: '#ffffff',
              cursor: isSubmitting ? 'not-allowed' : 'pointer',
              fontSize: '16px',
              fontWeight: '500',
              borderRadius: '0.625rem',
              transition: 'all 0.2s ease-in-out',
              boxShadow: '0 4px 6px -1px rgba(37, 99, 235, 0.1), 0 2px 4px -1px rgba(37, 99, 235, 0.06)'
            }}
            onMouseEnter={(e) => {
              if (!isSubmitting) {
                e.currentTarget.style.backgroundColor = '#1d4ed8';
                e.currentTarget.style.transform = 'translateY(-1px)';
                e.currentTarget.style.boxShadow = '0 4px 12px rgba(37, 99, 235, 0.25)';
              }
            }}
            onMouseLeave={(e) => {
              if (!isSubmitting) {
                e.currentTarget.style.backgroundColor = '#2563EB';
                e.currentTarget.style.transform = 'translateY(0)';
                e.currentTarget.style.boxShadow = '0 4px 6px -1px rgba(37, 99, 235, 0.1), 0 2px 4px -1px rgba(37, 99, 235, 0.06)';
              }
            }}
          >
            {isSubmitting ? 'Resetting Password...' : 'Reset Password'}
          </button>
        </div>

        {/* Back to Login Link */}
        <div className="text-center mt-4">
          <a
            href="/"
            className="text-decoration-none d-inline-flex align-items-center gap-2"
            onClick={(e) => {
              e.preventDefault();
              navigate("/");
            }}
            style={{ 
              color: '#6b7280', 
              fontSize: '14px',
              transition: 'color 0.2s ease-in-out'
            }}
            onMouseEnter={(e) => e.currentTarget.style.color = '#2563EB'}
            onMouseLeave={(e) => e.currentTarget.style.color = '#6b7280'}
          >
            ← Back to Login
          </a>
        </div>
      </div>
    </div>
  );
};

export default ResetPasswordPage;
